import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore, enableNetwork, disableNetwork } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";


const firebaseConfig = {
  apiKey: "AIzaSyAT06maMfyonsYuo56kXMcJPyP_M_FtkXY",
  authDomain: "nexus-aus.firebaseapp.com",
  projectId: "nexus-aus",
  storageBucket: "nexus-aus.firebasestorage.app",
  messagingSenderId: "1066388975171",
  appId: "1:1066388975171:web:62893d9b075c355104aef6",
  measurementId: "G-Q8VDWYRTP3"

};

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);
export const functions = getFunctions(firebaseApp, 'us-central1');

// Connection Handler Class
class FirebaseConnectionHandler {
  constructor() {
    this.isOnline = navigator.onLine;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.setupEventListeners();
  }

  setupEventListeners() {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    
    // Listen for visibility changes to reconnect when app becomes visible
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  async handleOnline() {
    this.isOnline = true;
    this.reconnectAttempts = 0;
    try {
      await enableNetwork(db);
      console.log('Firebase reconnected - network enabled');
    } catch (error) {
      console.error('Failed to reconnect Firebase:', error);
    }
  }

  async handleOffline() {
    this.isOnline = false;
    try {
      await disableNetwork(db);
      console.log('Firebase disconnected - network disabled');
    } catch (error) {
      console.error('Failed to disconnect Firebase:', error);
    }
  }

  async handleVisibilityChange() {
    if (!document.hidden && navigator.onLine) {
      // App became visible and we're online - try to reconnect
      await this.forceReconnect();
    }
  }

  async forceReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    
    this.reconnectAttempts++;
    try {
      await disableNetwork(db);
      await new Promise(resolve => setTimeout(resolve, 1000));
      await enableNetwork(db);
      console.log('Firebase force reconnected');
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('Force reconnect failed:', error);
    }
  }

  async retryOperation(operation, maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        const isNetworkError = error.code === 'unavailable' || 
                             error.message.includes('offline') ||
                             error.message.includes('network') ||
                             error.code === 'deadline-exceeded';
        
        if (attempt === maxRetries || !isNetworkError) {
          throw error;
        }
        
        const delay = 1000 * Math.pow(2, attempt - 1);
        console.log(`Firebase operation failed (attempt ${attempt}/${maxRetries}). Retrying in ${delay}ms...`);
        
        // Try to force reconnect on network errors
        if (isNetworkError && navigator.onLine) {
          await this.forceReconnect();
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
}

// Create singleton instance
export const connectionHandler = new FirebaseConnectionHandler();

// Legacy exports (keep for backward compatibility)
export const enableFirebaseNetwork = async () => {
  try {
    await enableNetwork(db);
    console.log('Firebase network enabled');
    return true;
  } catch (error) {
    console.log('Error enabling Firebase network:', error);
    return false;
  }
};

export const disableFirebaseNetwork = async () => {
  try {
    await disableNetwork(db);
    console.log('Firebase network disabled');
    return true;
  } catch (error) {
    console.log('Error disabling Firebase network:', error);
    return false;
  }
};

// Enhanced retry operation using the connection handler
export const retryFirebaseOperation = async (operation, maxRetries = 3, baseDelay = 1000) => {
  return connectionHandler.retryOperation(operation, maxRetries);
};

// Connection status checker
export const checkFirebaseConnection = async () => {
  try {
    await enableNetwork(db);
    return true;
  } catch (error) {
    return false;
  }
};

// Initialize offline support when the app starts
let offlineSupportInitialized = false;
export const initializeOfflineSupport = async () => {
  if (offlineSupportInitialized) return;
  
  try {
    await enableNetwork(db);
    offlineSupportInitialized = true;
    console.log('Firebase offline support initialized');
  } catch (error) {
    console.log('Firebase offline support initialization failed:', error);
  }
};

// Auto-initialize when module loads
initializeOfflineSupport();